/**
 * Notification and thinking sounds using the Web Audio API.
 * Settings are read from localStorage so users can customize volume, style and on/off states.
 * No external files required — all sounds are generated in-browser.
 */
import { unlockAudio } from './audioUnlock';

const SETTINGS_KEYS = {
  thinkingEnabled: 'cogency_sound_thinking_enabled',
  dingEnabled: 'cogency_sound_ding_enabled',
  volume: 'cogency_sound_volume',
  thinkingInterval: 'cogency_sound_thinking_interval'
};

const DEFAULT_SETTINGS = {
  thinkingEnabled: true,
  dingEnabled: true,
  volume: 0.7,
  thinkingInterval: 1.5
};

let audioCtx = null;
let thinkingInterval = null;
let thinkingTimeout = null;

export function getSoundSettings() {
  try {
    const thinkingEnabled = localStorage.getItem(SETTINGS_KEYS.thinkingEnabled);
    const dingEnabled = localStorage.getItem(SETTINGS_KEYS.dingEnabled);
    const volume = localStorage.getItem(SETTINGS_KEYS.volume);
    const thinkingIntervalVal = localStorage.getItem(SETTINGS_KEYS.thinkingInterval);
    const waitToneSpeed = localStorage.getItem('cogency_sound_wait_tone_speed');
    return {
      thinkingEnabled: thinkingEnabled === null ? DEFAULT_SETTINGS.thinkingEnabled : thinkingEnabled === 'true',
      dingEnabled: dingEnabled === null ? DEFAULT_SETTINGS.dingEnabled : dingEnabled === 'true',
      volume: volume === null ? DEFAULT_SETTINGS.volume : Math.max(0, Math.min(1, parseFloat(volume))),
      thinkingInterval: thinkingIntervalVal === null ? DEFAULT_SETTINGS.thinkingInterval : Math.max(0.3, Math.min(3, parseFloat(thinkingIntervalVal))),
      waitToneSpeed: waitToneSpeed === null ? 1.0 : Math.max(0.5, Math.min(2, parseFloat(waitToneSpeed)))
    };
  } catch {
    return { ...DEFAULT_SETTINGS, waitToneSpeed: 1.0 };
  }
}

export function saveSoundSettings(settings) {
  try {
    localStorage.setItem(SETTINGS_KEYS.thinkingEnabled, String(settings.thinkingEnabled));
    localStorage.setItem(SETTINGS_KEYS.dingEnabled, String(settings.dingEnabled));
    localStorage.setItem(SETTINGS_KEYS.volume, String(settings.volume));
    localStorage.setItem(SETTINGS_KEYS.thinkingInterval, String(settings.thinkingInterval));
    if (typeof settings.waitToneSpeed === 'number') {
      localStorage.setItem('cogency_sound_wait_tone_speed', String(settings.waitToneSpeed));
    }
  } catch {
    // ignore
  }
}

function getAudioContext() {
  unlockAudio();
  if (!audioCtx) {
    audioCtx = new (window.AudioContext || window.webkitAudioContext)();
  }
  if (audioCtx.state === 'suspended') {
    audioCtx.resume().catch(() => {});
  }
  return audioCtx;
}

function playTone({ frequency, duration, type = 'sine', volume, startTime }) {
  const ctx = getAudioContext();
  const now = startTime || ctx.currentTime;
  const osc = ctx.createOscillator();
  const gain = ctx.createGain();
  osc.type = type;
  if (Array.isArray(frequency)) {
    osc.frequency.setValueAtTime(frequency[0], now);
    for (let i = 1; i < frequency.length; i += 2) {
      osc.frequency.exponentialRampToValueAtTime(frequency[i], now + frequency[i + 1]);
    }
  } else {
    osc.frequency.setValueAtTime(frequency, now);
  }
  gain.gain.setValueAtTime(0.0001, now);
  gain.gain.exponentialRampToValueAtTime(volume, now + 0.01);
  gain.gain.exponentialRampToValueAtTime(0.0001, now + duration);
  osc.connect(gain);
  gain.connect(ctx.destination);
  osc.start(now);
  osc.stop(now + duration);
}

export function playNotificationSound() {
  try {
    const { volume } = getSoundSettings();
    const now = getAudioContext().currentTime;
    playTone({ frequency: [523.25, 659.25, 0.1], duration: 0.5, volume: volume * 0.5, startTime: now });
    playTone({ frequency: [659.25, 783.99, 0.18], duration: 0.55, volume: volume * 0.35, startTime: now + 0.08 });
  } catch {
    // ignore
  }
}

export function playAnswerDing() {
  try {
    const { dingEnabled, volume } = getSoundSettings();
    if (!dingEnabled) return;
    const now = getAudioContext().currentTime;
    playTone({ frequency: [659.25, 987.77, 0.1], duration: 0.6, volume, startTime: now });
    playTone({ frequency: 987.77, duration: 0.7, volume: volume * 0.6, startTime: now + 0.05 });
  } catch {
    // ignore
  }
}

// Varying agent tones — cycled through so each tick sounds a little different
const AGENT_TONES = [880, 660, 1100, 550, 1320, 770];

const THINKING_CONFIG = {
  clickDuration: 0.08,
  maxDuration: 10000,
  type: 'triangle',
  getFrequency: (index) => AGENT_TONES[index % AGENT_TONES.length]
};

function playThinkingClick(ctx, time, frequency, volume, duration, type) {
  const osc = ctx.createOscillator();
  const gain = ctx.createGain();
  osc.type = type;
  osc.frequency.setValueAtTime(frequency, time);
  gain.gain.setValueAtTime(0.0001, time);
  gain.gain.exponentialRampToValueAtTime(volume, time + 0.01);
  gain.gain.exponentialRampToValueAtTime(0.0001, time + duration);
  osc.connect(gain);
  gain.connect(ctx.destination);
  osc.start(time);
  osc.stop(time + duration);
}

export function startThinkingSound() {
  stopThinkingSound();
  try {
    const { thinkingEnabled, volume, thinkingInterval: intervalSeconds } = getSoundSettings();
    if (!thinkingEnabled) return;

    const ctx = getAudioContext();
    let tickIndex = 0;

    const tick = () => {
      const now = ctx.currentTime;
      const freq = THINKING_CONFIG.getFrequency(tickIndex);
      playThinkingClick(ctx, now, freq, volume, THINKING_CONFIG.clickDuration, THINKING_CONFIG.type);
      tickIndex += 1;
    };

    tick();
    const intervalMs = Math.max(300, intervalSeconds * 1000);
    thinkingInterval = setInterval(tick, intervalMs);
    thinkingTimeout = setTimeout(() => {
      stopThinkingSound();
    }, THINKING_CONFIG.maxDuration);
  } catch {
    // ignore
  }
}

export function stopThinkingSound() {
  if (thinkingInterval) {
    clearInterval(thinkingInterval);
    thinkingInterval = null;
  }
  if (thinkingTimeout) {
    clearTimeout(thinkingTimeout);
    thinkingTimeout = null;
  }
}

export function previewThinkingSound() {
  stopThinkingSound();
  try {
    const { volume, thinkingInterval: intervalSeconds } = getSoundSettings();
    const ctx = getAudioContext();
    let tickIndex = 0;
    const previewTicks = 4;

    const tick = () => {
      if (tickIndex >= previewTicks) return;
      const now = ctx.currentTime;
      const freq = THINKING_CONFIG.getFrequency(tickIndex);
      playThinkingClick(ctx, now, freq, volume, THINKING_CONFIG.clickDuration, THINKING_CONFIG.type);
      tickIndex += 1;
      if (tickIndex < previewTicks) {
        setTimeout(tick, intervalSeconds * 1000);
      }
    };

    tick();
  } catch {
    // ignore
  }
}

export function previewAnswerDing() {
  playAnswerDing();
}
